import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

type GetAllocationGroupByUserResponse =
  components['schemas']['GetAllocationGroupByUserEndpoint_GetAllocationGroupByUserEndpointPagedResponse'];

export interface UseAllocationGroupByUserReturn extends BaseQuery {
  allocationGroups: GetAllocationGroupByUserResponse | null;
  totalCount: number;
}

export function useAllocationGroupByUser(
  userId: string,
  page: number = 1,
  pageSize: number = 50,
  enabled: boolean = true,
): UseAllocationGroupByUserReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getAllocationGroupByUser = async (page: number, pageSize: number, organizationId: number, userId: string) => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/users/{userId}/allocation-groups', {
      params: {
        path: { organizationId, userId: userId },
        query: {
          Page: page,
          PageSize: pageSize,
        },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetAllocationGroupByUserResponse;
  };

  const isQueryEnabled = enabled && !!userId && organizationId > 0;

  const useAllocationGroupByUserQuery = useQuery({
    queryKey: ['allocationGroups', page, pageSize, organizationId, userId],
    queryFn: () => getAllocationGroupByUser(page, pageSize, organizationId, userId),
    enabled: isQueryEnabled,
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = useAllocationGroupByUserQuery;

  return {
    allocationGroups: data ?? null,
    totalCount: data?.totalCount ?? 0,
    isLoading: isQueryEnabled && (isLoading || isPending),
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
